import React, { useEffect, useMemo, useState } from 'react';
import { Table, Form, Button, Alert, Spinner, Nav } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  fetchCaseBilling,
  createCaseBillingEntry,
  updateCaseBillingEntry,
  deleteCaseBillingEntry,
} from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { TimeEntry, PaymentEntry, BillingTotals } from '../../types';
import { PAYMENT_METHOD_OPTIONS, PAYMENT_TYPE_OPTIONS } from '../../constants/constants';

const today = () => new Date().toISOString().slice(0, 10);

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  if (!m) return d;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const byDateDesc = <T extends { entry_date: string; created_at: string }>(a: T, b: T) =>
  b.entry_date.localeCompare(a.entry_date) || b.created_at.localeCompare(a.created_at);

const errorMessage = (err: any) => err.response?.data?.error || err.message;

const emptyTime = (staff: string, rate: string) => ({
  entry_date: today(), staff, hours: '', rate, billable: true, description: '',
});

const emptyPayment = () => ({
  entry_date: today(), amount: '', payment_type: 'Payment', method: 'Check', reference: '', payer: '', note: '',
});

const Stat: React.FC<{ label: string; value: string; sub?: string; tone?: string }> = ({ label, value, sub, tone }) => (
  <div>
    <div style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500, marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontWeight: 600, fontSize: '1rem', color: tone || '#111827' }}>{value}</div>
    {sub && <div className="small text-muted">{sub}</div>}
  </div>
);

const CaseBilling: React.FC = () => {
  const { case_id } = useParams<{ case_id: string }>();
  const auth = useAuth();
  const userEmail: string = auth.user?.profile?.email || '';

  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [totals, setTotals] = useState<BillingTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'time' | 'payments'>('time');

  const [timeForm, setTimeForm] = useState(emptyTime(userEmail, ''));
  const [paymentForm, setPaymentForm] = useState(emptyPayment());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // New time entries default to the rate used most recently on this case.
  const lastRate = useMemo(() => {
    const withRate = [...timeEntries].sort(byDateDesc).find(e => e.rate);
    return withRate?.rate ? String(withRate.rate) : '';
  }, [timeEntries]);

  const load = async () => {
    try {
      const data = await fetchCaseBilling(case_id!, auth.user?.id_token);
      setTimeEntries(data.time_entries);
      setPayments(data.payments);
      setTotals(data.totals);
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.id_token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    if (!editingId) setTimeForm(f => (f.rate || f.hours || f.description ? f : { ...f, rate: lastRate }));
  }, [lastRate, editingId]);

  const resetForms = () => {
    setEditingId(null);
    setTimeForm(emptyTime(userEmail, lastRate));
    setPaymentForm(emptyPayment());
  };

  const switchView = (v: 'time' | 'payments') => {
    resetForms();
    setView(v);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = view === 'time'
        ? { entry_type: 'time' as const, ...timeForm, hours: Number(timeForm.hours), rate: timeForm.rate === '' ? undefined : Number(timeForm.rate) }
        : { entry_type: 'payment' as const, ...paymentForm, amount: Number(paymentForm.amount) };
      if (editingId) {
        await updateCaseBillingEntry(case_id!, editingId, body, auth.user?.id_token);
      } else {
        await createCaseBillingEntry(case_id!, body, auth.user?.id_token);
      }
      resetForms();
      await load();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const startEditTime = (t: TimeEntry) => {
    setEditingId(t.entry_id);
    setTimeForm({
      entry_date: t.entry_date, staff: t.staff, hours: String(t.hours),
      rate: t.rate != null ? String(t.rate) : '', billable: t.billable, description: t.description,
    });
  };

  const startEditPayment = (p: PaymentEntry) => {
    setEditingId(p.entry_id);
    setPaymentForm({
      entry_date: p.entry_date, amount: String(p.amount), payment_type: p.payment_type, method: p.method,
      reference: p.reference || '', payer: p.payer || '', note: p.note || '',
    });
  };

  const handleDelete = async (entry_id: string) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    setDeletingId(entry_id);
    try {
      await deleteCaseBillingEntry(case_id!, entry_id, auth.user?.id_token);
      if (editingId === entry_id) resetForms();
      await load();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const rowActions = (entry_id: string, onEdit: () => void) => (
    <div className="d-flex gap-2 flex-nowrap">
      <Button variant="link" size="sm" className="p-0" onClick={onEdit}>Edit</Button>
      <Button variant="link" size="sm" className="p-0 text-danger" disabled={deletingId === entry_id}
        onClick={() => handleDelete(entry_id)}>
        {deletingId === entry_id ? '...' : 'Delete'}
      </Button>
    </div>
  );

  const timeValid = Number(timeForm.hours) > 0 && timeForm.description.trim() !== '';
  const paymentValid = Number(paymentForm.amount) > 0;

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span>Loading time &amp; payments...</span>
      </div>
    );
  }

  const balance = totals?.balance ?? 0;

  return (
    <div>
      {totals && (
        <div className="d-flex gap-5 flex-wrap mb-3">
          <Stat label="Hours" value={totals.billable_hours.toFixed(2)}
            sub={totals.total_hours !== totals.billable_hours ? `${totals.total_hours.toFixed(2)} total` : 'billable'} />
          <Stat label="Billed" value={money(totals.billed_amount)} sub="billable hours × rate" />
          <Stat label="Received" value={money(totals.received_amount)} />
          <Stat
            label={balance > 0 ? 'Balance due' : balance < 0 ? 'Retainer remaining' : 'Balance'}
            value={money(Math.abs(balance))}
            tone={balance > 0 ? '#b45309' : balance < 0 ? '#047857' : undefined}
          />
        </div>
      )}

      <Nav variant="tabs" className="mb-3" activeKey={view} onSelect={(k) => switchView((k as 'time' | 'payments') || 'time')}>
        <Nav.Item><Nav.Link eventKey="time">Time ({timeEntries.length})</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="payments">Payments ({payments.length})</Nav.Link></Nav.Item>
      </Nav>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Form onSubmit={submit} className="mb-3">
        {view === 'time' ? (
          <div className="d-flex gap-2 align-items-start flex-wrap">
            <Form.Control type="date" size="sm" style={{ maxWidth: 150 }} value={timeForm.entry_date}
              onChange={(e) => setTimeForm({ ...timeForm, entry_date: e.target.value })} />
            <Form.Control type="text" size="sm" style={{ maxWidth: 190 }} placeholder="Staff" value={timeForm.staff}
              onChange={(e) => setTimeForm({ ...timeForm, staff: e.target.value })} />
            <Form.Control type="number" size="sm" style={{ maxWidth: 90 }} placeholder="Hours" min="0" step="0.1"
              value={timeForm.hours} onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })} />
            <Form.Control type="number" size="sm" style={{ maxWidth: 110 }} placeholder="Rate $/hr" min="0" step="1"
              value={timeForm.rate} onChange={(e) => setTimeForm({ ...timeForm, rate: e.target.value })} />
            <Form.Check type="checkbox" id="billable-check" label="Billable" className="small mt-1"
              checked={timeForm.billable} onChange={(e) => setTimeForm({ ...timeForm, billable: e.target.checked })} />
            <Form.Control as="textarea" rows={1} size="sm" style={{ flex: '1 1 260px' }}
              placeholder="What was done (e.g. reviewed 2023 bank statements)..."
              value={timeForm.description} onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })} />
            <Button type="submit" size="sm" variant="primary" disabled={!timeValid || saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </Button>
            {editingId && <Button size="sm" variant="outline-secondary" onClick={resetForms} disabled={saving}>Cancel</Button>}
          </div>
        ) : (
          <div className="d-flex gap-2 align-items-start flex-wrap">
            <Form.Control type="date" size="sm" style={{ maxWidth: 150 }} value={paymentForm.entry_date}
              onChange={(e) => setPaymentForm({ ...paymentForm, entry_date: e.target.value })} />
            <Form.Control type="number" size="sm" style={{ maxWidth: 120 }} placeholder="Amount $" min="0" step="0.01"
              value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
            <Form.Select size="sm" style={{ maxWidth: 120 }} value={paymentForm.payment_type}
              onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}>
              {PAYMENT_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
            <Form.Select size="sm" style={{ maxWidth: 130 }} value={paymentForm.method}
              onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}>
              {PAYMENT_METHOD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
            <Form.Control type="text" size="sm" style={{ maxWidth: 130 }} placeholder="Check # / ref"
              value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} />
            <Form.Control type="text" size="sm" style={{ maxWidth: 180 }} placeholder="Paid by"
              value={paymentForm.payer} onChange={(e) => setPaymentForm({ ...paymentForm, payer: e.target.value })} />
            <Form.Control type="text" size="sm" style={{ flex: '1 1 200px' }} placeholder="Note"
              value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
            <Button type="submit" size="sm" variant="primary" disabled={!paymentValid || saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </Button>
            {editingId && <Button size="sm" variant="outline-secondary" onClick={resetForms} disabled={saving}>Cancel</Button>}
          </div>
        )}
      </Form>

      {view === 'time' ? (
        timeEntries.length === 0 ? (
          <Alert variant="secondary" className="mb-0">No time logged yet.</Alert>
        ) : (
          <div className="table-responsive">
            <Table striped hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Date</th><th>Staff</th><th className="text-end">Hours</th><th className="text-end">Rate</th>
                  <th className="text-end">Amount</th><th>Description</th><th></th>
                </tr>
              </thead>
              <tbody>
                {[...timeEntries].sort(byDateDesc).map(t => (
                  <tr key={t.entry_id} className={editingId === t.entry_id ? 'table-warning' : undefined}>
                    <td className="small text-nowrap">{formatDate(t.entry_date)}</td>
                    <td className="small text-muted text-nowrap">{t.staff}</td>
                    <td className="small text-end">{t.hours.toFixed(2)}</td>
                    <td className="small text-end">{t.rate != null ? money(t.rate) : '—'}</td>
                    <td className="small text-end">
                      {!t.billable ? <span className="text-muted fst-italic">non-billable</span>
                        : t.rate != null ? money(t.hours * t.rate) : '—'}
                    </td>
                    <td className="small" style={{ whiteSpace: 'pre-wrap' }}>{t.description}</td>
                    <td>{rowActions(t.entry_id, () => startEditTime(t))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )
      ) : (
        payments.length === 0 ? (
          <Alert variant="secondary" className="mb-0">No payments recorded yet.</Alert>
        ) : (
          <div className="table-responsive">
            <Table striped hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th className="text-end">Amount</th><th>Method</th>
                  <th>Ref</th><th>Paid by</th><th>Note</th><th></th>
                </tr>
              </thead>
              <tbody>
                {[...payments].sort(byDateDesc).map(p => (
                  <tr key={p.entry_id} className={editingId === p.entry_id ? 'table-warning' : undefined}>
                    <td className="small text-nowrap">{formatDate(p.entry_date)}</td>
                    <td className="small">{p.payment_type}</td>
                    <td className={`small text-end ${p.payment_type === 'Refund' ? 'text-danger' : ''}`}>
                      {p.payment_type === 'Refund' ? `(${money(p.amount)})` : money(p.amount)}
                    </td>
                    <td className="small">{p.method}</td>
                    <td className="small">{p.reference || '—'}</td>
                    <td className="small">{p.payer || '—'}</td>
                    <td className="small">{p.note || ''}</td>
                    <td>{rowActions(p.entry_id, () => startEditPayment(p))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )
      )}
    </div>
  );
};

export default CaseBilling;
